import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import BrandLogo from '../components/BrandLogo';
import { detectRegion } from '../utils/countryDetection';

const REGION_INTRO = {
  us: 'These Terms of Service apply to users in the United States.',
  eu: 'These Terms of Service apply to users in the European Economic Area.',
  in: 'These Terms of Service apply to users in India.',
  global: 'These Terms of Service apply globally to all users of LoopVisitor.',
};

const sectionTitle = {
  fontSize: 22,
  fontWeight: 700,
  marginTop: 40,
  marginBottom: 14,
  color: '#1E293B',
};

const subSectionTitle = {
  fontSize: 18,
  fontWeight: 600,
  marginTop: 28,
  marginBottom: 10,
  color: '#334155',
};

const paragraph = {
  lineHeight: 1.85,
  marginBottom: 16,
  color: '#374151',
  fontSize: 15,
};

const listStyle = {
  lineHeight: 1.85,
  marginBottom: 16,
  paddingLeft: 24,
  color: '#374151',
  fontSize: 15,
};

const listItem = {
  marginBottom: 8,
};

function show(region, ...targets) {
  return region === 'global' || targets.includes(region);
}

export default function Terms() {
  const region = useMemo(() => detectRegion(), []);

  return (
    <div style={{ maxWidth: 780, margin: '60px auto', padding: '0 24px 80px' }}>
      <div style={{ marginBottom: 24 }}>
        <BrandLogo className="landing-logo" />
      </div>

      <h1 style={{ fontSize: 30, fontWeight: 700, marginBottom: 8, color: '#0F172A' }}>Terms of Service</h1>
      <p style={{ color: '#64748B', lineHeight: 1.8, marginBottom: 8 }}>
        <strong>Effective Date:</strong> June 13, 2026
      </p>
      <p style={{ color: '#64748B', lineHeight: 1.8, marginBottom: 24 }}>
        <strong>Last Updated:</strong> June 13, 2026
      </p>

      <div style={{ background: '#F0F9FF', border: '1px solid #BAE6FD', borderRadius: 8, padding: '16px 20px', marginBottom: 32 }}>
        <p style={{ ...paragraph, marginBottom: 0, color: '#0C4A6E', fontSize: 14 }}>
          {REGION_INTRO[region]} Please read these Terms carefully before using LoopVisitor. By creating an account or using our Service, you agree to be bound by these Terms. If you do not agree, do not use the Service.
        </p>
      </div>

      {/* ── 1. AGREEMENT TO TERMS ── */}
      <h2 style={sectionTitle}>1. Agreement to Terms</h2>
      <p style={paragraph}>
        These Terms of Service ("<strong>Terms</strong>") constitute a legally binding agreement between you ("<strong>you</strong>," "<strong>your</strong>," or "<strong>Business User</strong>") and LoopVisitor ("<strong>LoopVisitor</strong>," "<strong>we</strong>," "<strong>our</strong>," or "<strong>us</strong>") governing your access to and use of the LoopVisitor platform, website at loopvisitor.com, APIs, and related services (collectively, the "<strong>Service</strong>").
      </p>
      <p style={paragraph}>
        By accessing or using the Service, you confirm that you have read, understood, and agree to these Terms and our{' '}
        <Link to="/privacy">Privacy Policy</Link>, which is incorporated herein by reference.
      </p>

      {/* ── 2. ELIGIBILITY ── */}
      <h2 style={sectionTitle}>2. Eligibility</h2>
      <ul style={listStyle}>
        <li style={listItem}>You must be at least <strong>18 years of age</strong> (or the age of majority in your jurisdiction) to use the Service.</li>
        <li style={listItem}>You must have the legal authority to enter into these Terms on behalf of yourself or the business entity you represent.</li>
        <li style={listItem}>If you are using the Service on behalf of an organization, you represent and warrant that you have the authority to bind that organization to these Terms.</li>
        <li style={listItem}>The Service is designed for <strong>business use</strong>. It is not intended for personal, family, or household use.</li>
      </ul>

      {/* ── 3. ACCOUNT REGISTRATION ── */}
      <h2 style={sectionTitle}>3. Account Registration and Security</h2>
      <p style={paragraph}>To use the Service, you must create an account. You agree to:</p>
      <ul style={listStyle}>
        <li style={listItem}>Provide accurate, current, and complete information during registration</li>
        <li style={listItem}>Maintain and promptly update your account information to keep it accurate</li>
        <li style={listItem}>Maintain the security and confidentiality of your login credentials</li>
        <li style={listItem}>Immediately notify us at support@loopvisitor.com of any unauthorized access to or use of your account</li>
        <li style={listItem}>Accept responsibility for all activities that occur under your account</li>
      </ul>
      <p style={paragraph}>
        We reserve the right to suspend or terminate accounts that contain inaccurate information or that we reasonably believe have been compromised.
      </p>

      {/* ── 4. SERVICE DESCRIPTION ── */}
      <h2 style={sectionTitle}>4. Service Description</h2>
      <p style={paragraph}>
        LoopVisitor is a customer engagement and retention platform for businesses. The Service enables you to:
      </p>
      <ul style={listStyle}>
        <li style={listItem}>Import and manage customer contact information</li>
        <li style={listItem}>Send automated follow-up messages via email{show(region, 'us') ? ', SMS,' : ''} and WhatsApp</li>
        <li style={listItem}>Request and track customer reviews</li>
        <li style={listItem}>Monitor engagement metrics including message delivery and link clicks</li>
        <li style={listItem}>Customize message templates for your industry</li>
      </ul>
      <p style={paragraph}>
        Feature availability varies by subscription plan and may be subject to feature flags, regional availability, and third-party service integrations.
      </p>

      {/* ── 5. SUBSCRIPTION PLANS, PAYMENT, AND BILLING ── */}
      <h2 style={sectionTitle}>5. Subscription Plans, Payment, and Billing</h2>

      <h3 style={subSectionTitle}>5.1 Plans and Pricing</h3>
      <p style={paragraph}>
        We offer a free plan and several paid subscription plans. Details of current plans, including pricing, usage limits, and included features, are available on our website.{' '}
        {show(region, 'in', 'us') ? 'Pricing may vary by region (e.g., India vs. USA). ' : ''}
        {show(region, 'eu') ? 'All prices shown to EEA users are inclusive of or exclusive of VAT as indicated at checkout. ' : ''}
        {show(region, 'in') ? 'All prices shown to Indian users are exclusive of GST unless otherwise stated. ' : ''}
      </p>

      <h3 style={subSectionTitle}>5.2 Billing</h3>
      <ul style={listStyle}>
        <li style={listItem}>Paid plans are shown as monthly subscriptions and are billed through our payment processor, Razorpay.</li>
        <li style={listItem}>For billing, renewal, entitlement, and quota calculations, one <strong>month</strong> means a rolling period of exactly <strong>30 consecutive days</strong> from activation or renewal. It is not a calendar month.</li>
        <li style={listItem}>By subscribing to a paid plan, you authorize us (via Razorpay) to charge the applicable fee to your chosen payment method on each billing cycle.</li>
        <li style={listItem}>All fees are quoted exclusive of applicable taxes unless otherwise stated. You are responsible for any applicable taxes{show(region, 'in') ? ' including GST' : ''}{show(region, 'eu') ? ' including VAT' : ''}{show(region, 'us') ? ' including state and local sales tax' : ''}.</li>
      </ul>

      <h3 style={subSectionTitle}>5.3 Plan Changes</h3>
      <ul style={listStyle}>
        <li style={listItem}><strong>Upgrades</strong> take effect after payment is confirmed and start a new 30-day billing cycle. Unused message allowance from the prior cycle is added to the new cycle.</li>
        <li style={listItem}><strong>Downgrades</strong> take effect at the start of the next billing cycle. You will retain access to your current plan's features until the end of the current period.</li>
        <li style={listItem}>When a downgrade takes effect, the lower plan's message quotas and customer-addition limit apply. Existing customer records and account data are retained; if the retained count is at or above the new limit, new additions are blocked until the count is below the limit or the plan is upgraded.</li>
      </ul>

      <h3 style={subSectionTitle}>5.4 Cancellation</h3>
      <ul style={listStyle}>
        <li style={listItem}>You may cancel your subscription at any time through the billing section of your dashboard.</li>
        <li style={listItem}>Upon cancellation, you will retain access to your paid plan features until the end of the current billing period.</li>
        <li style={listItem}>After the billing period ends, your account will revert to the free plan.</li>
        <li style={listItem}>Cancellation does not automatically delete your account or data. See our <Link to="/privacy">Privacy Policy</Link> for data retention details.</li>
      </ul>

      <h3 style={subSectionTitle}>5.5 Refunds</h3>
      <p style={paragraph}>
        Refund requests are handled on a case-by-case basis. If you believe you are entitled to a refund due to a billing error or service issue, please contact us at support@loopvisitor.com within 15 days of the charge. We reserve the right to approve or deny refund requests at our reasonable discretion.
      </p>
      {show(region, 'eu') && (
        <p style={paragraph}>
          <strong>EU Right of Withdrawal:</strong> If you are a consumer in the EEA, you may have a right to withdraw from a digital service contract within 14 days of purchase. By using the Service immediately upon subscription, you acknowledge and consent that you may lose this right of withdrawal once the Service has been fully provided during the withdrawal period.
        </p>
      )}
      {show(region, 'in') && (
        <p style={paragraph}>
          <strong>Consumer Rights:</strong> Refund rights under the Consumer Protection Act, 2019 and Consumer Protection (E-Commerce) Rules, 2020 are not affected by these Terms.
        </p>
      )}

      {/* ── 6. YOUR RESPONSIBILITIES AND ACCEPTABLE USE ── */}
      <h2 style={sectionTitle}>6. Your Responsibilities and Acceptable Use</h2>

      <h3 style={subSectionTitle}>6.1 General Conduct</h3>
      <p style={paragraph}>You agree to use the Service only for lawful purposes and in accordance with these Terms. You shall not:</p>
      <ul style={listStyle}>
        <li style={listItem}>Use the Service to send unsolicited messages (spam), harass, threaten, or abuse any person</li>
        <li style={listItem}>Send messages to individuals who have not consented to receive communications from your business</li>
        <li style={listItem}>Use the Service for any fraudulent, deceptive, or misleading activity</li>
        <li style={listItem}>Transmit malware, viruses, or other harmful code</li>
        <li style={listItem}>Attempt to gain unauthorized access to the Service, other accounts, or our systems</li>
        <li style={listItem}>Reverse engineer, decompile, or disassemble any part of the Service</li>
        <li style={listItem}>Use the Service in any manner that could damage, disable, or impair the Service</li>
        <li style={listItem}>Resell, sublicense, or provide the Service to third parties without our written consent</li>
        <li style={listItem}>Upload content that infringes any third party's intellectual property, privacy, or other rights</li>
      </ul>

      <h3 style={subSectionTitle}>6.2 Compliance with Messaging Laws</h3>
      <p style={paragraph}>
        You are solely responsible for ensuring that all messages sent through the Service comply with applicable laws and regulations, including but not limited to:
      </p>
      <ul style={listStyle}>
        {show(region, 'us') && (
          <>
            <li style={listItem}><strong>CAN-SPAM Act</strong> (United States) — including accurate header information, honest subject lines, clear identification as advertising, a valid physical postal address, and a functioning unsubscribe mechanism</li>
            <li style={listItem}><strong>Telephone Consumer Protection Act (TCPA)</strong> (United States) — including obtaining prior express written consent before sending SMS or making automated calls</li>
          </>
        )}
        {show(region, 'eu') && (
          <>
            <li style={listItem}><strong>General Data Protection Regulation (GDPR)</strong> (European Union / EEA) — including lawful basis for processing, data minimization, and honoring data subject rights</li>
            <li style={listItem}><strong>ePrivacy Directive</strong> (EU) — including requirements for consent to electronic communications and cookie usage</li>
          </>
        )}
        {show(region, 'in') && (
          <>
            <li style={listItem}><strong>Digital Personal Data Protection Act, 2023</strong> (India) — including obtaining valid consent before processing personal data</li>
            <li style={listItem}><strong>Telecom Commercial Communications Customer Preference Regulations (TCCPR)</strong> (India / TRAI) — including respecting Do Not Disturb (DND) registrations and time-of-day restrictions for commercial communications</li>
          </>
        )}
        <li style={listItem}>Any other applicable anti-spam, data protection, or telecommunications law in your jurisdiction{show(region, 'us') ? ', including CASL (Canada) if you message Canadian recipients' : ''}</li>
      </ul>
      <p style={paragraph}>
        LoopVisitor provides tools such as unsubscribe links and opt-out mechanisms to help you comply. However, <strong>compliance is your responsibility</strong>. We are not liable for your failure to comply with applicable laws.
      </p>

      <h3 style={subSectionTitle}>6.3 Customer Data Obligations</h3>
      <p style={paragraph}>When you import or enter End Customer data into LoopVisitor, you represent and warrant that:</p>
      <ul style={listStyle}>
        <li style={listItem}>You have obtained all necessary consents and authorizations to collect, store, and process such data</li>
        <li style={listItem}>You have provided appropriate privacy notices to your End Customers</li>
        <li style={listItem}>The data is accurate, lawfully obtained, and you have the right to share it with LoopVisitor for processing</li>
        <li style={listItem}>You will promptly honor all unsubscribe, deletion, and data access requests from your End Customers</li>
        <li style={listItem}>You will not import data of minors (individuals under 18 or the applicable age of majority)</li>
        {show(region, 'eu') && (
          <li style={listItem}>You will maintain a record of processing activities as required under GDPR Article 30, and you will enter into a Data Processing Agreement with LoopVisitor upon request</li>
        )}
        {show(region, 'in') && (
          <li style={listItem}>You will ensure that consent obtained from End Customers is free, specific, informed, unconditional, and unambiguous as required under the DPDP Act, 2023</li>
        )}
      </ul>

      {/* ── 7. INTELLECTUAL PROPERTY ── */}
      <h2 style={sectionTitle}>7. Intellectual Property</h2>

      <h3 style={subSectionTitle}>7.1 Our Intellectual Property</h3>
      <p style={paragraph}>
        The Service, including its design, features, code, documentation, logos, trademarks, and all related intellectual property, is and remains the exclusive property of LoopVisitor. These Terms do not grant you any right, title, or interest in the Service beyond the limited right to use it in accordance with these Terms.
      </p>

      <h3 style={subSectionTitle}>7.2 Your Content</h3>
      <p style={paragraph}>
        You retain ownership of all data, content, and materials you upload to the Service ("<strong>Your Content</strong>"). By using the Service, you grant us a limited, non-exclusive, worldwide license to use, process, and store Your Content solely to provide and improve the Service. This license terminates when you delete Your Content or your account.
      </p>

      <h3 style={subSectionTitle}>7.3 Feedback</h3>
      <p style={paragraph}>
        If you provide us with suggestions, ideas, or feedback about the Service, you grant us an unrestricted, irrevocable, worldwide license to use such feedback without any obligation to you.
      </p>

      {/* ── 8. THIRD-PARTY SERVICES ── */}
      <h2 style={sectionTitle}>8. Third-Party Services</h2>
      <p style={paragraph}>
        The Service integrates with or relies on third-party services including Razorpay (payments), Resend (email), Brevo (SMS), Meta/WhatsApp (messaging), and Calendly (scheduling). Your use of these services is subject to their respective terms and privacy policies. We are not responsible for the availability, accuracy, or practices of third-party services.
      </p>
      <p style={paragraph}>
        If you connect your WhatsApp Business account through Meta's Embedded Signup, your use of the WhatsApp Business API is subject to{' '}
        <a href="https://www.whatsapp.com/legal/business-terms/" target="_blank" rel="noopener noreferrer">WhatsApp's Business Terms</a>{' '}
        and{' '}
        <a href="https://www.facebook.com/legal/terms" target="_blank" rel="noopener noreferrer">Meta's Terms of Service</a>.
      </p>

      {/* ── 9. SERVICE AVAILABILITY AND MODIFICATIONS ── */}
      <h2 style={sectionTitle}>9. Service Availability and Modifications</h2>
      <ul style={listStyle}>
        <li style={listItem}>We strive to maintain high availability of the Service but do not guarantee uninterrupted or error-free operation.</li>
        <li style={listItem}>The Service may be temporarily unavailable due to maintenance, updates, or circumstances beyond our control.</li>
        <li style={listItem}>We reserve the right to modify, suspend, or discontinue any part of the Service at any time, with reasonable notice where practicable.</li>
        <li style={listItem}>We will make commercially reasonable efforts to notify you of material changes that affect your use of the Service.</li>
      </ul>

      {/* ── 10. USAGE LIMITS ── */}
      <h2 style={sectionTitle}>10. Usage Limits</h2>
      <p style={paragraph}>
        Each subscription plan includes message-delivery limits by channel. Customer storage, templates, analytics, and automation configuration are not plan-gated. If you exceed a message limit:
      </p>
      <ul style={listStyle}>
        <li style={listItem}>We may throttle or temporarily suspend message sending until the next billing cycle or until you upgrade your plan</li>
        <li style={listItem}>We will notify you when you are approaching or have reached your limits</li>
        <li style={listItem}>Persistent or intentional overuse may result in account suspension</li>
      </ul>

      {/* ── 11. DISCLAIMER OF WARRANTIES ── */}
      <h2 style={sectionTitle}>11. Disclaimer of Warranties</h2>
      <p style={paragraph}>
        <strong>THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, WHETHER EXPRESS, IMPLIED, OR STATUTORY.</strong> To the maximum extent permitted by applicable law, we disclaim all warranties, including but not limited to:
      </p>
      <ul style={listStyle}>
        <li style={listItem}>Implied warranties of merchantability, fitness for a particular purpose, and non-infringement</li>
        <li style={listItem}>Any warranty that the Service will be uninterrupted, timely, secure, or error-free</li>
        <li style={listItem}>Any warranty regarding the accuracy or reliability of results obtained through the Service</li>
        <li style={listItem}>Any warranty that messages sent through the Service will be delivered (delivery depends on third-party providers, recipient settings, and other factors beyond our control)</li>
      </ul>
      {show(region, 'eu') && (
        <p style={paragraph}>
          If you are a consumer in the EEA, this disclaimer does not affect your statutory rights under applicable EU consumer protection laws, including the Consumer Rights Directive (2011/83/EU).
        </p>
      )}
      {show(region, 'in') && (
        <p style={paragraph}>
          This disclaimer does not affect your statutory rights under the Consumer Protection Act, 2019 (India).
        </p>
      )}

      {/* ── 12. LIMITATION OF LIABILITY ── */}
      <h2 style={sectionTitle}>12. Limitation of Liability</h2>
      <p style={paragraph}>
        <strong>TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW:</strong>
      </p>
      <ul style={listStyle}>
        <li style={listItem}>
          LoopVisitor shall not be liable for any <strong>indirect, incidental, special, consequential, or punitive damages</strong>, including but not limited to loss of profits, revenue, data, business opportunities, goodwill, or anticipated savings, arising out of or in connection with your use of (or inability to use) the Service.
        </li>
        <li style={listItem}>
          Our total aggregate liability to you for any and all claims arising under or in connection with these Terms or the Service shall not exceed the <strong>greater of (a) the total fees paid by you to LoopVisitor in the 12 months preceding the claim, or (b) USD $100</strong> (or the equivalent in your local currency).
        </li>
        <li style={listItem}>
          These limitations apply regardless of the theory of liability (contract, tort, negligence, strict liability, or otherwise) and even if we have been advised of the possibility of such damages.
        </li>
      </ul>
      <p style={paragraph}>
        {show(region, 'in') && 'Some provisions of Indian law, including the Consumer Protection Act, 2019, do not allow the exclusion or limitation of certain damages. In such cases, our liability shall be limited to the maximum extent permitted by law. '}
        {show(region, 'eu') && 'Under EU law, liability for damages caused by intentional misconduct or gross negligence cannot be excluded. These limitations do not affect any liability that cannot be excluded or limited under applicable EEA consumer protection laws. '}
        {show(region, 'us') && 'Some US states do not allow the exclusion or limitation of incidental or consequential damages. In such states, our liability shall be limited to the maximum extent permitted by law.'}
      </p>

      {/* ── 13. INDEMNIFICATION ── */}
      <h2 style={sectionTitle}>13. Indemnification</h2>
      <p style={paragraph}>
        You agree to indemnify, defend, and hold harmless LoopVisitor and its officers, directors, employees, and agents from and against any and all claims, damages, losses, liabilities, costs, and expenses (including reasonable attorneys' fees) arising out of or related to:
      </p>
      <ul style={listStyle}>
        <li style={listItem}>Your use of the Service in violation of these Terms</li>
        <li style={listItem}>Your violation of any applicable law or regulation, including anti-spam, data protection, and telecommunications laws</li>
        <li style={listItem}>Any messages you send through the Service that violate the rights of third parties</li>
        <li style={listItem}>Your failure to obtain required consents from End Customers</li>
        <li style={listItem}>Any claim by an End Customer or third party related to Your Content or your use of their data</li>
      </ul>
      {show(region, 'eu') && (
        <p style={paragraph}>
          This indemnification clause applies only to the extent permitted under applicable EEA consumer protection laws and does not limit your statutory rights.
        </p>
      )}

      {/* ── 14. TERMINATION ── */}
      <h2 style={sectionTitle}>14. Termination</h2>

      <h3 style={subSectionTitle}>14.1 Termination by You</h3>
      <p style={paragraph}>
        You may close your account at any time by contacting us at support@loopvisitor.com. If you have an active paid subscription, cancellation will take effect at the end of the current billing period.
      </p>

      <h3 style={subSectionTitle}>14.2 Termination by Us</h3>
      <p style={paragraph}>We may suspend or terminate your access to the Service, with or without notice, if:</p>
      <ul style={listStyle}>
        <li style={listItem}>You breach these Terms or our <Link to="/privacy">Privacy Policy</Link></li>
        <li style={listItem}>We reasonably believe your use of the Service is fraudulent, abusive, or harmful to other users or third parties</li>
        <li style={listItem}>You fail to pay applicable fees after reasonable notice</li>
        <li style={listItem}>Required by law, regulation, or court order</li>
        <li style={listItem}>We discontinue the Service (with at least 30 days' notice where practicable)</li>
      </ul>

      <h3 style={subSectionTitle}>14.3 Effect of Termination</h3>
      <ul style={listStyle}>
        <li style={listItem}>Upon termination, your right to use the Service ceases immediately</li>
        <li style={listItem}>We will retain your data for the period described in our <Link to="/privacy">Privacy Policy</Link></li>
        <li style={listItem}>Sections that by their nature should survive termination (including Limitation of Liability, Indemnification, Governing Law, and Intellectual Property) shall survive</li>
        <li style={listItem}>No refunds will be issued for unused portions of prepaid fees, unless required by applicable law</li>
      </ul>

      {/* ── 15. GOVERNING LAW AND DISPUTE RESOLUTION ── */}
      <h2 style={sectionTitle}>15. Governing Law and Dispute Resolution</h2>

      {region === 'in' && (
        <>
          <h3 style={subSectionTitle}>15.1 Governing Law</h3>
          <p style={paragraph}>
            These Terms shall be governed by and construed in accordance with the laws of <strong>India</strong>, without regard to its conflict-of-law principles.
          </p>
          <h3 style={subSectionTitle}>15.2 Dispute Resolution</h3>
          <p style={paragraph}>In the event of any dispute arising out of or relating to these Terms or the Service:</p>
          <ul style={listStyle}>
            <li style={listItem}>
              <strong>Informal Resolution:</strong> You agree to first attempt to resolve the dispute informally by contacting us at support@loopvisitor.com. We will make good-faith efforts to resolve the matter within 30 days.
            </li>
            <li style={listItem}>
              <strong>Arbitration:</strong> If informal resolution fails, the dispute shall be referred to and finally resolved by arbitration in accordance with the Arbitration and Conciliation Act, 1996 (India). The arbitration shall be conducted by a sole arbitrator, in English, with the seat of arbitration in New Delhi, India.
            </li>
            <li style={listItem}>
              <strong>Jurisdiction:</strong> For matters not subject to arbitration, or where applicable law requires, the courts of New Delhi, India shall have exclusive jurisdiction.
            </li>
          </ul>
          <h3 style={subSectionTitle}>15.3 Consumer Protection</h3>
          <p style={paragraph}>
            Nothing in these Terms shall deprive you of any mandatory consumer protection rights granted under the Consumer Protection Act, 2019 (India) or any rules made thereunder, including the Consumer Protection (E-Commerce) Rules, 2020. You retain the right to approach the appropriate Consumer Disputes Redressal Commission.
          </p>
        </>
      )}

      {region === 'us' && (
        <>
          <h3 style={subSectionTitle}>15.1 Governing Law</h3>
          <p style={paragraph}>
            These Terms shall be governed by and construed in accordance with the laws of <strong>India</strong>, without regard to its conflict-of-law principles, except where superseded by mandatory US federal or state laws applicable to you.
          </p>
          <h3 style={subSectionTitle}>15.2 Dispute Resolution</h3>
          <p style={paragraph}>In the event of any dispute arising out of or relating to these Terms or the Service:</p>
          <ul style={listStyle}>
            <li style={listItem}>
              <strong>Informal Resolution:</strong> You agree to first attempt to resolve the dispute informally by contacting us at support@loopvisitor.com. We will make good-faith efforts to resolve the matter within 30 days.
            </li>
            <li style={listItem}>
              <strong>Arbitration:</strong> If informal resolution fails, the dispute shall be resolved by binding arbitration administered under commercially reasonable rules, conducted by a single arbitrator, in English. The seat of arbitration shall be New Delhi, India, unless otherwise required by applicable US law.
            </li>
            <li style={listItem}>
              <strong>Small Claims:</strong> Either party may bring qualifying claims in small claims court in your county of residence (or, if a business, your principal place of business).
            </li>
          </ul>
          <h3 style={subSectionTitle}>15.3 Class Action Waiver</h3>
          <p style={paragraph}>
            To the extent permitted by law, you agree that any disputes will be resolved on an <strong>individual basis only</strong>. You waive the right to participate in any class action, collective action, or representative proceeding against LoopVisitor.
          </p>
          <h3 style={subSectionTitle}>15.4 Consumer Protection</h3>
          <p style={paragraph}>
            Nothing in these Terms shall deprive you of any mandatory consumer protection rights granted under the laws of your state of residence.
          </p>
        </>
      )}

      {region === 'eu' && (
        <>
          <h3 style={subSectionTitle}>15.1 Governing Law</h3>
          <p style={paragraph}>
            These Terms shall be governed by and construed in accordance with the laws of <strong>India</strong>, without regard to its conflict-of-law principles. However, if you are a consumer in the EEA, you shall also benefit from any mandatory provisions of the law of the country in which you are resident.
          </p>
          <h3 style={subSectionTitle}>15.2 Dispute Resolution</h3>
          <p style={paragraph}>In the event of any dispute arising out of or relating to these Terms or the Service:</p>
          <ul style={listStyle}>
            <li style={listItem}>
              <strong>Informal Resolution:</strong> You agree to first attempt to resolve the dispute informally by contacting us at support@loopvisitor.com. We will make good-faith efforts to resolve the matter within 30 days.
            </li>
            <li style={listItem}>
              <strong>Arbitration:</strong> If informal resolution fails, the dispute shall be referred to and finally resolved by arbitration, conducted by a single arbitrator, in English, with the seat of arbitration in New Delhi, India.
            </li>
            <li style={listItem}>
              <strong>Jurisdiction:</strong> For matters not subject to arbitration, or where applicable law requires, the courts of New Delhi, India shall have jurisdiction, without prejudice to your right to bring proceedings in the courts of your country of residence if you are a consumer.
            </li>
          </ul>
          <h3 style={subSectionTitle}>15.3 Consumer Rights</h3>
          <p style={paragraph}>
            Nothing in these Terms shall affect your statutory rights as a consumer under applicable EU law, including the Consumer Rights Directive (2011/83/EU) and the Unfair Contract Terms Directive (93/13/EEC). If you are a consumer in the EEA, you may also use the European Commission's Online Dispute Resolution (ODR) platform at{' '}
            <a href="https://ec.europa.eu/consumers/odr" target="_blank" rel="noopener noreferrer">https://ec.europa.eu/consumers/odr</a>.
          </p>
        </>
      )}

      {region === 'global' && (
        <>
          <h3 style={subSectionTitle}>15.1 Governing Law</h3>
          <p style={paragraph}>
            These Terms shall be governed by and construed in accordance with the laws of <strong>India</strong>, without regard to its conflict-of-law principles, except where superseded by mandatory local laws (such as consumer protection statutes in your jurisdiction).
          </p>
          <h3 style={subSectionTitle}>15.2 Dispute Resolution</h3>
          <p style={paragraph}>In the event of any dispute arising out of or relating to these Terms or the Service:</p>
          <ul style={listStyle}>
            <li style={listItem}>
              <strong>Informal Resolution:</strong> You agree to first attempt to resolve the dispute informally by contacting us at support@loopvisitor.com. We will make good-faith efforts to resolve the matter within 30 days.
            </li>
            <li style={listItem}>
              <strong>Arbitration (India):</strong> If informal resolution fails, the dispute shall be referred to and finally resolved by arbitration in accordance with the Arbitration and Conciliation Act, 1996 (India). The arbitration shall be conducted by a sole arbitrator, in English, with the seat of arbitration in New Delhi, India.
            </li>
            <li style={listItem}>
              <strong>Jurisdiction:</strong> For matters not subject to arbitration, or where applicable law requires, the courts of New Delhi, India shall have exclusive jurisdiction.
            </li>
          </ul>
          <h3 style={subSectionTitle}>15.3 US-Specific Provisions</h3>
          <ul style={listStyle}>
            <li style={listItem}>
              <strong>Class Action Waiver:</strong> To the extent permitted by law, you agree that any disputes will be resolved on an individual basis and you waive the right to participate in any class action, collective action, or representative proceeding.
            </li>
            <li style={listItem}>
              <strong>Small Claims:</strong> Either party may bring qualifying claims in small claims court in your county of residence (or, if a business, your principal place of business).
            </li>
          </ul>
          <h3 style={subSectionTitle}>15.4 Consumer Protection Savings Clause</h3>
          <p style={paragraph}>
            Nothing in these Terms shall deprive you of any mandatory consumer protection rights granted under the laws of your country or state of residence, including rights under India's Consumer Protection Act, 2019 and applicable EU consumer protection directives.
          </p>
        </>
      )}

      {/* ── 16. CHANGES TO THESE TERMS ── */}
      <h2 style={sectionTitle}>16. Changes to These Terms</h2>
      <p style={paragraph}>
        We may modify these Terms from time to time. When we make material changes, we will:
      </p>
      <ul style={listStyle}>
        <li style={listItem}>Post the updated Terms on our website with a revised "Last Updated" date</li>
        <li style={listItem}>Provide at least 30 days' notice before material changes take effect (via email or in-app notification)</li>
      </ul>
      <p style={paragraph}>
        Your continued use of the Service after the effective date of updated Terms constitutes your acceptance of the changes. If you do not agree with the updated Terms, you must stop using the Service and close your account.
      </p>

      {/* ── 17. GENERAL PROVISIONS ── */}
      <h2 style={sectionTitle}>17. General Provisions</h2>
      <ul style={listStyle}>
        <li style={listItem}>
          <strong>Entire Agreement:</strong> These Terms, together with our Privacy Policy, constitute the entire agreement between you and LoopVisitor regarding the Service and supersede all prior agreements, communications, and understandings.
        </li>
        <li style={listItem}>
          <strong>Severability:</strong> If any provision of these Terms is found to be invalid or unenforceable, that provision shall be limited or eliminated to the minimum extent necessary, and the remaining provisions shall remain in full force and effect.
        </li>
        <li style={listItem}>
          <strong>Waiver:</strong> Our failure to enforce any right or provision of these Terms shall not constitute a waiver of that right or provision.
        </li>
        <li style={listItem}>
          <strong>Assignment:</strong> You may not assign or transfer your rights under these Terms without our prior written consent. We may assign our rights and obligations under these Terms without restriction.
        </li>
        <li style={listItem}>
          <strong>Force Majeure:</strong> We shall not be liable for any failure or delay in performance resulting from causes beyond our reasonable control, including natural disasters, war, terrorism, pandemics, government actions, power failures, internet disruptions, or third-party service outages.
        </li>
        <li style={listItem}>
          <strong>No Agency:</strong> Nothing in these Terms creates a partnership, joint venture, employment, or agency relationship between you and LoopVisitor.
        </li>
        <li style={listItem}>
          <strong>Language:</strong> These Terms are drafted in English. In the event of any conflict between translated versions and the English version, the English version shall prevail.
        </li>
      </ul>

      {/* ── 18. CONTACT US ── */}
      <h2 style={sectionTitle}>18. Contact Us</h2>
      <p style={paragraph}>
        If you have any questions or concerns about these Terms, please contact us:
      </p>
      <ul style={listStyle}>
        <li style={listItem}><strong>Email:</strong> support@loopvisitor.com</li>
        <li style={listItem}><strong>Website:</strong> loopvisitor.com</li>
      </ul>

      <div style={{ marginTop: 40, padding: '16px 20px', background: '#F8FAFC', borderRadius: 8, border: '1px solid #E2E8F0' }}>
        <p style={{ ...paragraph, marginBottom: 0, fontSize: 13, color: '#64748B' }}>
          By creating an account or using LoopVisitor, you acknowledge that you have read, understood, and agree to be bound by these Terms of Service and our{' '}
          <Link to="/privacy">Privacy Policy</Link>.
        </p>
      </div>
    </div>
  );
}
